import { Injectable, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { Observable, catchError, map, of } from 'rxjs';

import { AiMedicalDocumentItem, MedicalDocumentItem } from '../models/document.interface';
import { createBlobUrl, getBlobFileType, getFileExtension } from '../utils/blob-utils';
import { MedicalDocumentsService } from './medical-documents.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentPreviewService {
  private readonly medicalDocumentsApi = inject(MedicalDocumentsService);
  private readonly sanitizer = inject(DomSanitizer);

  previewDocument(document: MedicalDocumentItem | AiMedicalDocumentItem, isAiDocument: boolean): Observable<SafeResourceUrl | null> {
    const documentType = this.getDocumentType(document);
    const aiDocument = document as AiMedicalDocumentItem;

    return (
      isAiDocument
        ? this.medicalDocumentsApi.fetchAIMedicalDocument(aiDocument.userId, aiDocument.id)
        : this.medicalDocumentsApi.fetchMemberDocument((document as MedicalDocumentItem).medicalRecordId, (document as MedicalDocumentItem).documentId)
    ).pipe(
      map(response => this.createPreviewUrl(response, documentType)),
      catchError(() => of(null))
    );
  }

  private createPreviewUrl(response: ArrayBuffer, documentType: string): SafeResourceUrl {
    const blobType = getBlobFileType(documentType);
    const blobUrl = createBlobUrl(response, blobType);

    if (documentType?.toLowerCase() === 'pdf') return this.sanitizer.bypassSecurityTrustResourceUrl(`${blobUrl}#view=FitH&toolbar=1&page=1`);
    else return this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
  }

  private getDocumentType(document: MedicalDocumentItem | AiMedicalDocumentItem): string {
    const fileName = this.isAIDocument(document) ? document.fileName : document.documentName;

    return getFileExtension(fileName);
  }

  private isAIDocument(document: MedicalDocumentItem | AiMedicalDocumentItem): document is AiMedicalDocumentItem {
    return 'userId' in document && 'fileName' in document && !('medicalRecordId' in document);
  }
}
